import React, { useContext, useMemo, useCallback, useState, useEffect } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withUnvalidatedControlErrors, withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import {
  entityIs,
  propertyIs,
  andComp,
} from '@bingads-webui-campaign-react/form-error';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  TextTitle,
  ValidatedPanel,
  getPanelErrorProps
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { BudgetComponent } from '@bingads-webui-campaign-react/budget-component';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
// eslint-disable-next-line
import BingAdsWebUIStaticLegacyPageContext from '@bingads-webui-static-legacy/page-context';
// eslint-disable-next-line
import { BudgetSuggestionDialog, useBudgetSuggestionRepository, MODE } from '@bingads-webui-campaign-react/budget-suggestion-v2';

import { ERROR_ENTITY } from '../../constants';
import { BudgetImg } from '../../media';
import { useLocalizeBudget } from '../../utils/use-localize-budget';
import { SectionShimmer, withShimmer } from '../shimmers';

const { BudgetRange } = BingAdsWebUIStaticLegacyPageContext;

const getStyles = () => ({
  budgetTitle: {
    fontSize: '18px',
    display: 'inline',
  },
});

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const budgetProp = andComp(entityIsCampaign, propertyIs('Budget'));
const ServerValidatedBudgetComponentWithShimmer =
  withShimmer(
    withControlErrorHandling(withUnvalidatedControlErrors(
      BudgetComponent,
      { filter: budgetProp }
    )),
    SectionShimmer
  );

export const BudgetPanel = (props) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const { CurrentAccount: { CurrencyCode } } = serverContext || {};
  const getResponsiveStyles = useResponsiveStyles(getStyles);
  const { localizeBudget } = useLocalizeBudget();

  const {
    key,
    isLoading,
    expand,
    onDataChange,
    data = {},
    ...rest
  } = props;

  const {
    budgetAmount,
    currency,
    campaignId,
    campaignType,
    showBudgetSuggestion,
    isCampaignSetting,
    campaign,
  } = data;

  const { DailyRange } = BudgetRange || {};

  const budgetSuggestion = useMemo(() => ({
    Currency: currency,
    GlobalMaximumBudget: { DailyBudget: DailyRange.CeilingValue ? DailyRange.CeilingValue : 3000000 },
    GlobalMinimumBudget: { DailyBudget: DailyRange.FloorValue ? DailyRange.FloorValue : 0.05 },
  }), [currency, DailyRange.CeilingValue, DailyRange.FloorValue]);


  const budgetTitleDetails = useMemo(() => i18n.getString(_TL_('{{budgetAmount}} {{currency}} per day'), { budgetAmount, currency }), [budgetAmount, currency, i18n]);

  const onFieldChange = useCallback((updatedData) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        ...updatedData,
      }),
    });
  }, [onDataChange]);

  const onBudgetChange = useCallback((newBudget, shouldLocalize = false) => {
    if (newBudget) {
      const budgetAmountValue = shouldLocalize ? localizeBudget(newBudget, currency) : newBudget;
      onFieldChange({ budgetAmount: budgetAmountValue });
    }
  }, [currency, onFieldChange, localizeBudget]);

  const onBudgetChangeFromBudgetSuggestion = useCallback((newBudget, shouldLocalize = false) => {
    if (newBudget && newBudget.Amount) {
      const budgetAmountValue = shouldLocalize ? localizeBudget(newBudget.Amount, currency) : newBudget.Amount;
      onFieldChange({ budgetAmount: budgetAmountValue });
    }
  }, [currency, onFieldChange, localizeBudget]);

  const onBudgetValidate = useCallback(({ isValid, messages }) => {
    onFieldChange({ isBudgetValid: isValid, budgetErrors: messages });
  }, [onFieldChange]);

  const [budgetSuggestionData, setBudgetSuggestionData] = useState(null);
  const {
    loadBudget,
  } = useBudgetSuggestionRepository({
    campaignId,
    mode: MODE.EDITING,
    campaignType,
    currency,
  });

  const activity = useContext(ActivityContext);

  useEffect(() => {
    if (showBudgetSuggestion && isCampaignSetting) {
      loadBudget({ campaign: {}, assetGroup: {} }).then((datas) => {
        setBudgetSuggestionData(datas);
      }).finally(() => {
      });
    }
  }, [isCampaignSetting, loadBudget, showBudgetSuggestion]);

  return (
    <ValidatedPanel
      key={key}
      title={
        <TextTitle
          icon={BudgetImg}
          titleKey={_TL_('Budget')}
          topicKey="pop_BA_PMAXLite_Budget"
          as="h2"
          headingClassName={classNames(getResponsiveStyles('budgetTitle'))}
          details={budgetTitleDetails}
        />
        }
      actuationProps={{
          expandKey: key,
          expand,
        }}
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      {...rest}
    >
      <ServerValidatedBudgetComponentWithShimmer
        showShimmer={isLoading}
        showDropdown={false}
        currency={CurrencyCode}
        budget={budgetAmount}
        onBudgetChange={onBudgetChange}
        isLoading={isLoading}
        onNotifyValidation={onBudgetValidate}
        withSlider={false}
        budgetSuggestion={budgetSuggestion}
      />

      {/* will check Delivery Status in campaign when MT added */}
      {/* {showBudgetSuggestion && (data.DeliveryStatus === 'BudgetSuggestions' || 'BudgetSuggestions' in data.DeliveryStatusDetails) && isCampaignSetting && */}
      {showBudgetSuggestion &&
      isCampaignSetting &&
      !_.isEmpty(budgetSuggestionData) &&
      <BudgetSuggestionDialog
        campaignId={campaignId}
        budget={data.Budget}
        currency={currency||CurrencyCode}
        onBudgetChange={onBudgetChangeFromBudgetSuggestion}
        i18n={i18n}
        activity={activity}
        campaign={campaign}
        minBudget={budgetSuggestion.GlobalMinimumBudget.DailyBudget}
        maxBudget={budgetSuggestion.GlobalMaximumBudget.DailyBudget}
        defaultBudget={budgetAmount}
        {...rest}
      />
     }
    </ValidatedPanel>
  );
};

BudgetPanel.propTypes = {
  key: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  expand: PropTypes.func.isRequired,
  data: PropTypes.shape({}),
  onDataChange: PropTypes.func.isRequired,
};

BudgetPanel.defaultProps = {
  isLoading: false,
  data: {},
};
